import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { rupee, formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { playSound, primeAudio } from "../lib/orderSounds";
import { enablePush, disablePush, isPushEnabled, pushSupported } from "../lib/pushNotifications";
import { ChefHat, LogOut, RefreshCw, Clock, CheckCircle2, Flame, PackageCheck, Truck, Ban, MessageSquare, User, StickyNote, History, FileText, FileSpreadsheet, Filter, BellRing, Bell, BellOff, MonitorSmartphone } from "lucide-react";
import logoLight from "../assets/logo.png";

const TABS = [
  { key: "pending", label: "Pending Orders", icon: Clock, match: (o) => o.status === "placed" },
  { key: "running", label: "Running Orders", icon: Flame, match: (o) => ["accepted", "preparing"].includes(o.status) },
  { key: "markout", label: "Mark Out Orders", icon: PackageCheck, match: (o) => o.status === "ready" },
  { key: "delivery", label: "Delivery Awaited", icon: Truck, match: (o) => o.status === "out_for_delivery" },
  { key: "stockout", label: "Stock Out Orders", icon: Ban, match: (o) => o.status === "stock_out" },
];

const STATUS_OPTIONS = ["all", "placed", "accepted", "preparing", "ready", "out_for_delivery", "delivered", "cancelled", "stock_out"];
const STATUS_LABEL = (s) => ({ placed: "Pending", accepted: "Accepted", preparing: "Preparing", ready: "Marked Out", out_for_delivery: "Delivery Awaited", delivered: "Delivered", cancelled: "Cancelled", stock_out: "Stock Out" }[s] || s);

const fmtTime = (t) => t ? new Date(t).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const mins = (a, b) => (!a || !b) ? null : Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
const fmtDur = (m) => m == null ? "—" : m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;

function HistoryView() {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    if (status && status !== "all") p.set("status", status);
    return p.toString();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/kitchen/orders/history?${buildQuery()}`); setRows(data); }
    catch (e) { toast.error("Failed to load order history"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, status]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (fmt) => {
    setDownloading(fmt);
    const q = buildQuery();
    try { await downloadFile(`/kitchen/orders/history/export?format=${fmt}${q ? "&" + q : ""}`, `order-history.${fmt === "pdf" ? "pdf" : "xlsx"}`); }
    catch (e) { toast.error("Download failed"); }
    finally { setDownloading(""); }
  };

  return (
    <div data-testid="chef-history-view">
      <div className="bg-white rounded-xl border border-black/5 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="history-from" className="border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="history-to" className="border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="history-status" className="border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold bg-white">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : STATUS_LABEL(s)}</option>)}
            </select>
          </div>
          <button onClick={load} data-testid="history-apply" className="flex items-center gap-1.5 bg-navy text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-navy-soft"><Filter size={14} /> Apply</button>
          <div className="flex-1" />
          <button onClick={() => download("pdf")} disabled={downloading === "pdf"} data-testid="history-download-pdf" className="flex items-center gap-1.5 text-sm font-semibold text-navy border border-navy/20 rounded-full px-4 py-2 hover:bg-beige disabled:opacity-60"><FileText size={15} /> {downloading === "pdf" ? "..." : "PDF"}</button>
          <button onClick={() => download("xlsx")} disabled={downloading === "xlsx"} data-testid="history-download-excel" className="flex items-center gap-1.5 text-sm font-semibold text-white rounded-full px-4 py-2 disabled:opacity-60" style={{ backgroundColor: "#1D6F42" }}><FileSpreadsheet size={15} /> {downloading === "xlsx" ? "..." : "Excel"}</button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">Showing last 6 months by default. {rows.length} order(s).</div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-navy text-cream text-left">
            <tr>
              <th className="p-3">Order #</th><th className="p-3">Customer</th><th className="p-3">Items</th>
              <th className="p-3 text-right">Value</th><th className="p-3">Status</th>
              <th className="p-3">Received</th><th className="p-3">Accepted</th><th className="p-3">Completed</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground" data-testid="history-empty">No orders found for these filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 hover:bg-beige/40" data-testid={`history-row-${r.order_number}`}>
                <td className="p-3 font-medium text-navy whitespace-nowrap">{r.order_number}</td>
                <td className="p-3">{r.customer_name}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-[260px]">{r.items_summary}</td>
                <td className="p-3 text-right font-medium text-navy whitespace-nowrap">{rupee(r.grand_total)}</td>
                <td className="p-3"><span className="text-[11px] bg-beige text-navy px-2 py-0.5 rounded-full whitespace-nowrap">{STATUS_LABEL(r.status)}</span></td>
                <td className="p-3 text-xs whitespace-nowrap">{fmtTime(r.received_at)}</td>
                <td className="p-3 text-xs whitespace-nowrap">{fmtTime(r.accepted_at)}</td>
                <td className="p-3 text-xs whitespace-nowrap">{fmtTime(r.completed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RemarkBox({ order, onSaved }) {
  const [val, setVal] = useState(order.chef_remark || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/kitchen/orders/${order.id}/remark`, { remark: val }); toast.success("Remark saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 flex items-center gap-2">
      <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add a remark for this order..." data-testid={`chef-remark-input-${order.order_number}`}
        className="flex-1 border border-black/15 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-gold" />
      <button onClick={save} disabled={saving} data-testid={`chef-remark-save-${order.order_number}`}
        className="text-xs font-semibold text-navy border border-navy/20 rounded-full px-3 py-1.5 hover:bg-beige disabled:opacity-60">Save</button>
    </div>
  );
}

function OrderCard({ order, onAction, onReload }) {
  const acceptDur = mins(order.created_at, order.accepted_at);
  const markoutDur = mins(order.accepted_at || order.created_at, order.marked_out_at);
  const actions = {
    placed: [{ a: "accept", label: "Accept Order", cls: "bg-navy text-cream" }, { a: "stock-out", label: "Stock Out", cls: "bg-white text-destructive border border-destructive/30" }],
    accepted: [{ a: "mark-out", label: "Mark Out", cls: "bg-gold text-navy" }, { a: "stock-out", label: "Stock Out", cls: "bg-white text-destructive border border-destructive/30" }],
    preparing: [{ a: "mark-out", label: "Mark Out", cls: "bg-gold text-navy" }, { a: "stock-out", label: "Stock Out", cls: "bg-white text-destructive border border-destructive/30" }],
    ready: [{ a: "awaiting-delivery", label: "Send for Delivery", cls: "bg-navy text-cream" }],
    out_for_delivery: [{ a: "delivered", label: "Mark Delivered", cls: "bg-green-700 text-white" }],
    stock_out: [],
  }[order.status] || [];

  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-4" data-testid={`chef-order-${order.order_number}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-serif text-lg text-navy">{order.order_number}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1"><User size={12} /> {order.customer?.name || "Guest"} · {order.customer?.phone}</div>
        </div>
        <div className="text-right">
          <div className="font-semibold text-navy">{rupee(order.grand_total)}</div>
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${order.order_type === "takeaway" ? "bg-gold/20 text-navy" : "bg-navy/10 text-navy"}`}>{order.order_type}</span>
        </div>
      </div>

      <div className="mt-3 space-y-1 border-y border-black/5 py-2.5">
        {order.items?.map((it, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-navy">{it.name}{it.variant ? ` (${it.variant})` : ""} × {it.quantity}</span>
            <span className="text-muted-foreground">{rupee(it.total)}</span>
          </div>
        ))}
      </div>

      {order.special_instructions && (
        <div className="mt-2 text-xs bg-gold/10 border border-gold/30 rounded-lg p-2 flex gap-1.5" data-testid={`chef-special-${order.order_number}`}>
          <MessageSquare size={13} className="text-gold shrink-0 mt-0.5" /> <span className="text-navy">{order.special_instructions}</span>
        </div>
      )}
      {order.admin_note && (
        <div className="mt-2 text-xs bg-navy/5 border border-navy/15 rounded-lg p-2 flex gap-1.5" data-testid={`chef-admin-note-${order.order_number}`}>
          <StickyNote size={13} className="text-navy shrink-0 mt-0.5" /> <span className="text-navy"><b>Admin note:</b> {order.admin_note}</span>
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div className="bg-beige rounded-lg p-2"><div className="text-muted-foreground">Received</div><div className="text-navy font-medium" data-testid={`chef-received-${order.order_number}`}>{fmtTime(order.created_at)}</div></div>
        <div className="bg-beige rounded-lg p-2"><div className="text-muted-foreground">Accepted in</div><div className="text-navy font-medium" data-testid={`chef-accepted-${order.order_number}`}>{order.accepted_at ? fmtDur(acceptDur) : "Awaiting"}</div></div>
        <div className="bg-beige rounded-lg p-2"><div className="text-muted-foreground">Marked out in</div><div className="text-navy font-medium" data-testid={`chef-markout-${order.order_number}`}>{order.marked_out_at ? fmtDur(markoutDur) : "—"}</div></div>
      </div>

      {actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map((ac) => (
            <button key={ac.a} onClick={() => onAction(order.id, ac.a)} data-testid={`chef-action-${ac.a}-${order.order_number}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full ${ac.cls} hover:opacity-90 transition-opacity`}>{ac.label}</button>
          ))}
        </div>
      )}
      <RemarkBox order={order} onSaved={onReload} />
    </div>
  );
}

export default function KitchenDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("pending");
  const [alertIds, setAlertIds] = useState([]);
  const [muted, setMuted] = useState(() => localStorage.getItem("bbs_chef_muted") === "1");
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const seenRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => { isPushEnabled().then(setPushOn).catch(() => {}); }, []);

  const togglePush = async () => {
    primeAudio();
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush("/kitchen"); setPushOn(false); toast.success("Desktop alerts turned off");
      } else {
        const r = await enablePush("/kitchen");
        if (r.ok) { setPushOn(true); toast.success("Desktop alerts on — you'll be notified even when off-screen"); }
        else toast.error(r.reason === "denied" ? "Notifications are blocked in your browser settings" : r.reason === "unsupported" ? "Not supported on this device/browser" : "Push notifications are not configured yet");
      }
    } catch (e) { toast.error("Could not update desktop alerts"); }
    finally { setPushBusy(false); }
  };

  const sendTestPush = async () => {
    try { const { data } = await api.post("/kitchen/push/test"); toast.success(`Test notification sent to ${data.sent} device(s)`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "kitchen_chef")) navigate("/kitchen/login");
  }, [user, loading]);

  const load = useCallback(async () => {
    try {
      const [m, o] = await Promise.all([api.get("/kitchen/me"), api.get("/kitchen/orders")]);
      setMe(m.data); setOrders(o.data);
      const pendingIds = o.data.filter((x) => x.status === "placed").map((x) => x.id);
      if (seenRef.current === null) {
        seenRef.current = new Set(pendingIds); // seed on first load, don't alert for pre-existing
      } else {
        const fresh = pendingIds.filter((id) => !seenRef.current.has(id));
        fresh.forEach((id) => seenRef.current.add(id));
        setAlertIds((prev) => {
          const merged = Array.from(new Set([...prev, ...fresh]));
          return merged.filter((id) => pendingIds.includes(id)); // drop ones no longer pending
        });
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (user?.role === "kitchen_chef") { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }
  }, [user, load]);

  // Continuous ring while there are new unacknowledged orders
  useEffect(() => {
    if (alertIds.length > 0 && !muted) {
      primeAudio();
      playSound("alarm");
      ringRef.current = setInterval(() => playSound("alarm"), 3000);
      return () => clearInterval(ringRef.current);
    }
  }, [alertIds.length, muted]);

  // Unlock audio on the first user gesture (autoplay policy)
  useEffect(() => {
    const unlock = () => primeAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const stopAlert = () => { primeAudio(); setAlertIds([]); };
  const toggleMute = () => {
    primeAudio();
    setMuted((m) => { const n = !m; localStorage.setItem("bbs_chef_muted", n ? "1" : "0"); return n; });
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/kitchen/orders/${id}/${action}`);
      setAlertIds((prev) => prev.filter((x) => x !== id));
      toast.success("Order updated"); load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doLogout = () => { logout(); navigate("/kitchen/login"); };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-cream text-muted-foreground">Loading...</div>;

  const alertCount = alertIds.length;
  const filtered = (() => { const t = TABS.find((x) => x.key === tab); return t ? orders.filter(t.match) : []; })();

  return (
    <div className="min-h-screen bg-beige" data-testid="kitchen-dashboard">
      <header className="bg-navy text-cream sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoLight} alt="Begum's" className="h-9 w-auto" />
            <div>
              <div className="font-serif text-cream text-sm leading-tight flex items-center gap-1.5"><ChefHat size={15} className="text-gold" /> Kitchen Dashboard</div>
              <div className="text-[11px] text-gold" data-testid="chef-kitchen-name">{me ? `${me.kitchen?.name || "All Orders"} · ${me.name}` : "Loading…"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {alertCount > 0 && (
              <button onClick={stopAlert} data-testid="chef-alert-badge" title="New orders! Click to silence"
                className="relative flex items-center gap-1.5 bg-destructive text-white text-xs font-bold px-3 py-2 rounded-full animate-pulse">
                <BellRing size={15} /> {alertCount} New
              </button>
            )}
            {pushSupported() && (
              <button onClick={togglePush} disabled={pushBusy} data-testid="chef-push-toggle" title="Desktop & mobile notifications (works off-screen)"
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full transition-colors disabled:opacity-60 ${pushOn ? "bg-gold text-navy" : "bg-white/10 text-cream/85 hover:text-gold"}`}>
                <MonitorSmartphone size={15} /> {pushBusy ? "…" : pushOn ? "Alerts On" : "Enable Alerts"}
              </button>
            )}
            {pushSupported() && pushOn && (
              <button onClick={sendTestPush} data-testid="chef-push-test" title="Send a test notification to this device"
                className="text-xs font-semibold px-3 py-2 rounded-full bg-white/10 text-cream/85 hover:text-gold">Test</button>
            )}
            <button onClick={toggleMute} data-testid="chef-mute-toggle" title={muted ? "Unmute alerts" : "Mute alerts"}
              className={`p-2 hover:text-gold ${muted ? "text-destructive" : "text-cream/80"}`}>
              {muted ? <BellOff size={17} /> : <Bell size={17} />}
            </button>
            <button onClick={load} data-testid="chef-refresh" className="p-2 text-cream/80 hover:text-gold"><RefreshCw size={17} /></button>
            <button onClick={doLogout} data-testid="chef-logout" className="flex items-center gap-1.5 text-sm text-cream/85 hover:text-destructive"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {alertCount > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-xl px-4 py-3" data-testid="chef-alert-banner">
            <BellRing size={20} className="text-destructive animate-bounce" />
            <span className="font-semibold text-navy text-sm">{alertCount} new order{alertCount > 1 ? "s" : ""} just landed{muted ? " (muted)" : " — ringing"}!</span>
            <div className="flex-1" />
            <button onClick={() => { setTab("pending"); }} data-testid="chef-alert-view" className="text-xs font-semibold text-cream bg-navy rounded-full px-3 py-1.5 hover:bg-navy-soft">View Pending</button>
            <button onClick={stopAlert} data-testid="chef-alert-stop" className="text-xs font-semibold text-destructive border border-destructive/40 rounded-full px-3 py-1.5 hover:bg-destructive/10">Stop Alert</button>
          </div>
        )}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-5">
          {TABS.map((t) => {
            const count = orders.filter(t.match).length;
            const active = tab === t.key;
            const pending = t.key === "pending" && alertCount > 0;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`chef-tab-${t.key}`}
                className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${active ? "bg-navy text-cream" : "bg-white text-navy hover:bg-white/70"}`}>
                <t.icon size={15} className={active ? "text-gold" : "text-navy/60"} /> {t.label}
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${pending ? "bg-destructive text-white animate-pulse" : active ? "bg-gold text-navy" : "bg-beige text-navy"}`}>{count}</span>
              </button>
            );
          })}
          <button onClick={() => setTab("history")} data-testid="chef-tab-history"
            className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "history" ? "bg-navy text-cream" : "bg-white text-navy hover:bg-white/70"}`}>
            <History size={15} className={tab === "history" ? "text-gold" : "text-navy/60"} /> Order History
          </button>
        </div>

        {tab === "history" ? (
          <HistoryView />
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-black/5 p-12 text-center text-muted-foreground" data-testid="chef-empty">
            <CheckCircle2 size={30} className="mx-auto text-gold mb-2" /> No orders in this list right now.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((o) => <OrderCard key={o.id} order={o} onAction={doAction} onReload={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}
